import { apiClient } from "@/config/api";
import { ApiResponse } from "@/types/status";

// Role interfaces
export interface Role {
    _id: string;
    name: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

export interface RoleWithCount extends Role {
    count: number;
}

export interface CreateRoleDto {
    name: string;
}

export interface UpdateRoleDto {
    name: string;
}

export interface CreatePermissionRoleDto {
    role: string;
    permission: Record<string, string[]>;
}

export interface UpdatePermissionRoleDto {
    role: string;
    permission: Record<string, string[]>;
}

export interface Permission {
    _id: string;
    role: string;
    action: string[];
    subject: string;
    createdAt: string;
    updatedAt: string;
    __v: number;
}

// Role API endpoints
const ROLE_ENDPOINTS = {
    ROLES: '/roles',
    GET_ALL: '/roles/get-all',
    PERMISSIONS: '/permissions',
    CREATE_PERMISSION_ROLE: '/permissions/create-permission-role',
    GET_PERMISSIONS_BY_ROLE: '/permissions/get-all-by-role-id',
    DELETE_PERMISSIONS_BY_ROLE: '/permissions/delete-by-role-id',
    UPDATE_PERMISSIONS_BY_ROLE: '/permissions/update-permission-by-role-id',
};

export const roleService = {
    // Lấy tất cả roles
    async getAllRoles(): Promise<ApiResponse<Role[]>> {
        const endpoint = ROLE_ENDPOINTS.GET_ALL;
        return apiClient.get(endpoint);
    },

    // Lấy tất cả roles với số lượng users (sử dụng endpoint chính)
    async getAllRolesWithCount(): Promise<ApiResponse<Role[]>> {
        const endpoint = ROLE_ENDPOINTS.GET_ALL;
        return apiClient.get(endpoint);
    },

    // Lấy role theo ID
    async getRoleById(id: string): Promise<ApiResponse<Role>> {
        const endpoint = `${ROLE_ENDPOINTS.ROLES}/${id}`;
        return apiClient.get(endpoint);
    },

    // Tạo role mới
    async createRole(data: CreateRoleDto): Promise<ApiResponse<Role>> {
        const endpoint = ROLE_ENDPOINTS.ROLES;
        return apiClient.post(endpoint, data);
    },

    // Cập nhật role
    async updateRole(id: string, data: UpdateRoleDto): Promise<ApiResponse<Role>> {
        const endpoint = `${ROLE_ENDPOINTS.ROLES}/${id}`;
        return apiClient.put(endpoint, data);
    },

    // Xóa role
    async deleteRole(id: string): Promise<ApiResponse<any>> {
        const endpoint = `${ROLE_ENDPOINTS.ROLES}/${id}`;
        return apiClient.delete(endpoint);
    },

    // Tạo role với permissions
    async createPermissionRole(data: CreatePermissionRoleDto): Promise<ApiResponse<{ role: Role; permissions: Permission[] }>> {
        const endpoint = ROLE_ENDPOINTS.CREATE_PERMISSION_ROLE;
        return apiClient.post(endpoint, data);
    },

    // Lấy permissions theo role ID
    async getPermissionsByRoleId(id: string): Promise<ApiResponse<Permission[]>> {
        const endpoint = `${ROLE_ENDPOINTS.GET_PERMISSIONS_BY_ROLE}/${id}`;
        return apiClient.get(endpoint);
    },

    // Xóa permissions theo role ID
    async deletePermissionsByRole(id: string): Promise<ApiResponse<any>> {
        const endpoint = `${ROLE_ENDPOINTS.DELETE_PERMISSIONS_BY_ROLE}/${id}`;
        return apiClient.delete(endpoint);
    },

    // Cập nhật permissions theo role ID
    async updatePermissionsByRoleId(id: string, data: UpdatePermissionRoleDto): Promise<ApiResponse<{ role: Role; permissions: Permission[] }>> {
        const endpoint = `${ROLE_ENDPOINTS.UPDATE_PERMISSIONS_BY_ROLE}/${id}`;
        return apiClient.put(endpoint, data);
    },
};
